import { useState, useEffect } from "react";

export const QuantityInput = ({
  value = 1,
  onChange,
  onBlur,
  min = 1,
  max = null,
  disabled = false,
  readonly = false,
  className = "",
  inputClassName = "",
  width = "70px",
  height = "36px",
}) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleDecrease = () => {
    const numValue = Number(localValue) || 0;
    const newValue = Math.max(min, numValue - 1);
    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleIncrease = () => {
    const numValue = Number(localValue) || 0;
    const newValue = numValue + 1;

    // Check max if provided
    if (max !== null && newValue > max) {
      return;
    }

    setLocalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    // Normalize value on blur
    const numValue = Number(localValue);
    let normalized = numValue;

    if (isNaN(numValue) || numValue < min) {
      normalized = min;
    }

    if (max !== null && numValue > max) {
      normalized = max;
    }

    setLocalValue(normalized);

    if (onBlur) {
      onBlur(normalized);
    }

    if (onChange && normalized !== localValue) {
      onChange(normalized);
    }
  };

  // Readonly display
  if (readonly) {
    return (
      <div
        className={`border rounded-md flex items-center justify-center bg-gray-100 text-gray-600 ${className}`}
        style={{ width, height }}
      >
        {localValue}
      </div>
    );
  }

  const isDecrementDisabled = disabled || Number(localValue) <= min;
  const isIncrementDisabled =
    disabled || (max !== null && Number(localValue) >= max);

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      {/* Decrease Button */}
      <button
        type="button"
        onClick={handleDecrease}
        disabled={isDecrementDisabled}
        className="w-9 h-9 border flex justify-center items-center rounded-md text-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
        style={{ height }}
      >
        −
      </button>

      {/* Input */}
      <input
        type="number"
        value={localValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        min={min}
        max={max !== null ? max : undefined}
        className={`border rounded-md text-center bg-white focus:border-blue-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${inputClassName}`}
        style={{
          width,
          height,
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
        }}
      />

      {/* Increase Button */}
      <button
        type="button"
        onClick={handleIncrease}
        disabled={isIncrementDisabled}
        className="w-9 h-9 border rounded-md text-lg flex justify-center items-center hover:bg-gray-100 disabled:opacity-40 disabled:cursor-default"
        style={{ height }}
      >
        +
      </button>
    </div>
  );
};

export default QuantityInput;
